import React, {useEffect, useState} from "react";
import {
    Card,
    Col,
    Progress,
    Row,
    Skeleton,
    Statistic,
    Table,
    DatePicker,
    Space,
    Alert,
    Button,
    Modal,
    InputNumber,
    message,
    Typography
} from "antd";
import {useTranslation} from "react-i18next";
import useCurrentOrg from "../helpers/useCurrentOrg";
import api from "../api";
import {ColumnChart} from 'react-chartkick'
import 'chartkick/chart.js'
import {DollarOutlined, ExclamationCircleOutlined, PlusOutlined, RobotOutlined} from "@ant-design/icons";
import User from "../helpers/User";

const {RangePicker} = DatePicker;
const {Text} = Typography;

const AiUsageReport = () => {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const [loading, setLoading] = useState(true);
    const [usageData, setUsageData] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [addBudgetModalOpen, setAddBudgetModalOpen] = useState(false);
    const [budgetAmount, setBudgetAmount] = useState(10);
    const [addingBudget, setAddingBudget] = useState(false);

    const fetchUsageData = () => {
        if (!currentOrg?.id) return;

        setLoading(true);
        let url = `/api/organizations/${currentOrg.id}/cai_usage`;
        if (dateRange && dateRange[0] && dateRange[1]) {
            url += `?start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
        }

        api(url).then(res => {
            setUsageData(res.data);
            setLoading(false);
        }).catch(err => {
            console.error('Error fetching CAI usage:', err);
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchUsageData();
    }, [currentOrg?.id, dateRange]);

    // Listen for real-time CAI budget updates via ActionCable
    useEffect(() => {
        const handler = (e) => {
            message.success(t('budget-updated-realtime', `Budget updated! $${e.detail.amount} added.`));
            fetchUsageData();
        };
        window.addEventListener('caiBudgetUpdated', handler);
        return () => window.removeEventListener('caiBudgetUpdated', handler);
    }, []);

    // Detect return from Stripe Checkout
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topupStatus = params.get('cai_topup');
        if (topupStatus === 'success') {
            message.success(t('budget-payment-success', 'Payment successful! Your budget will be updated shortly.'));
            // Clean up URL
            const url = new URL(window.location);
            url.searchParams.delete('cai_topup');
            window.history.replaceState({}, '', url.toString());
        } else if (topupStatus === 'canceled') {
            message.info(t('budget-payment-canceled', 'Payment was canceled.'));
            const url = new URL(window.location);
            url.searchParams.delete('cai_topup');
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    const handleAddBudget = async () => {
        if (!budgetAmount || budgetAmount <= 0) {
            message.error(t('invalid-amount', 'Please enter a valid amount'));
            return;
        }

        setAddingBudget(true);
        try {
            const response = await api.post(`/api/organizations/${currentOrg.id}/add_cai_budget`, {
                amount: budgetAmount
            });

            if (response.data.success && response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                message.error(response.data.error || t('error-adding-budget', 'Error adding budget'));
                setAddingBudget(false);
            }
        } catch (err) {
            message.error(err.response?.data?.error || t('error-adding-budget', 'Error adding budget'));
            setAddingBudget(false);
        }
    };

    const getProgressStatus = () => {
        if (!usageData) return 'normal';
        if (usageData.budget_exceeded) return 'exception';
        if (usageData.at_warning_threshold) return 'warning';
        return 'normal';
    };

    const getProgressColor = () => {
        const status = getProgressStatus();
        if (status === 'exception') return '#ff4d4f';
        if (status === 'warning') return '#faad14';
        return '#1890ff';
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4
        }).format(value || 0);
    };

    const formatDailyData = () => {
        if (!usageData?.daily) return [];
        return Object.entries(usageData.daily).map(([date, cost]) => [date, cost]);
    };

    const modelColumns = [
        {
            title: t('model', 'Model'),
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => record.name || record.id
        },
        {
            title: t('count', 'Count'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => (a.count || 0) - (b.count || 0)
        },
        {
            title: t('cost', 'Cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost) => formatCurrency(cost),
            sorter: (a, b) => a.cost - b.cost,
            defaultSortOrder: 'descend'
        }
    ];

    const fieldColumns = [
        {
            title: t('field', 'Field'),
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => record.name || `Field #${record.id}`
        },
        {
            title: t('count', 'Count'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => (a.count || 0) - (b.count || 0)
        },
        {
            title: t('cost', 'Cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost) => formatCurrency(cost),
            sorter: (a, b) => a.cost - b.cost,
            defaultSortOrder: 'descend'
        }
    ];

    const userColumns = [
        {
            title: t('user', 'User'),
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => <User user={record} />
        },
        {
            title: t('count', 'Count'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => (a.count || 0) - (b.count || 0)
        },
        {
            title: t('cost', 'Cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost) => formatCurrency(cost),
            sorter: (a, b) => a.cost - b.cost,
            defaultSortOrder: 'descend'
        }
    ];

    const transformBreakdownData = (dataWithNames) => {
        if (!dataWithNames) return [];
        // Data is now an array of {id, name, cost} objects from the API
        return Array.isArray(dataWithNames) ? dataWithNames : [];
    };

    if (!currentOrg?.cai_enabled) {
        return (
            <Alert
                message={t('composable-ai-not-enabled', 'Composable AI Not Enabled')}
                description={t('composable-ai-not-enabled-description', 'Composable AI features are not enabled for this organization. Contact support to enable Composable AI.')}
                type="info"
                showIcon
                icon={<RobotOutlined />}
            />
        );
    }

    return (
        <Skeleton loading={loading}>
            <Space direction="vertical" size="large" style={{width: '100%'}}>
                {/* Budget Alert */}
                {usageData?.budget_exceeded && (
                    <Alert
                        message={t('composable-ai-budget-exceeded', 'Composable AI Budget Exceeded')}
                        description={t('composable-ai-budget-exceeded-description', 'Your Composable AI budget has been exceeded. AI operations are currently blocked. Please add more budget to continue.')}
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        action={
                            <Button type="primary" danger onClick={() => setAddBudgetModalOpen(true)}>
                                {t('add-budget', 'Add Budget')}
                            </Button>
                        }
                    />
                )}

                {usageData?.at_warning_threshold && !usageData?.budget_exceeded && (
                    <Alert
                        message={t('composable-ai-budget-warning', 'Composable AI Budget Warning')}
                        description={t('composable-ai-budget-warning-description', `You've used ${usageData.usage_percentage}% of your Composable AI budget. Consider adding more budget soon.`)}
                        type="warning"
                        showIcon
                    />
                )}

                {/* Date Range Filter */}
                <Card>
                    <Space>
                        <Text strong>{t('date-range', 'Date Range')}:</Text>
                        <RangePicker
                            onChange={setDateRange}
                            value={dateRange}
                            allowClear
                        />
                        <Button onClick={() => setDateRange(null)}>{t('clear', 'Clear')}</Button>
                    </Space>
                </Card>

                {/* Budget Overview */}
                <Card title={t('budget-overview', 'Budget Overview')}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <div style={{padding: '20px'}}>
                                <Text type="secondary">{t('usage-progress', 'Usage Progress')}</Text>
                                <Progress
                                    percent={usageData?.usage_percentage || 0}
                                    status={getProgressStatus()}
                                    strokeColor={getProgressColor()}
                                    format={(percent) => `${(parseFloat(percent) || 0).toFixed(1)}%`}
                                    size="default"
                                    style={{marginTop: '10px'}}
                                />
                                <div style={{marginTop: '10px'}}>
                                    <Text>
                                        {formatCurrency(usageData?.spent_amount)} / {formatCurrency(usageData?.total_budget)}
                                    </Text>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title={t('total-budget', 'Total Budget')}
                                        value={usageData?.total_budget || 0}
                                        prefix="$"
                                        precision={2}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title={t('spent', 'Spent')}
                                        value={usageData?.spent_amount || 0}
                                        prefix="$"
                                        precision={4}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title={t('remaining', 'Remaining')}
                                        value={usageData?.remaining_budget || 0}
                                        prefix="$"
                                        precision={2}
                                        valueStyle={{
                                            color: usageData?.budget_exceeded ? '#ff4d4f' :
                                                   usageData?.at_warning_threshold ? '#faad14' : '#52c41a'
                                        }}
                                    />
                                </Col>
                            </Row>
                            <div style={{marginTop: '20px'}}>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setAddBudgetModalOpen(true)}
                                >
                                    {t('add-budget', 'Add Budget')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Daily Usage Chart */}
                {usageData?.daily && Object.keys(usageData.daily).length > 0 && (
                    <Card title={t('daily-usage', 'Daily Usage')}>
                        <ColumnChart
                            data={formatDailyData()}
                            prefix="$"
                            height="300px"
                            colors={['#1890ff']}
                        />
                    </Card>
                )}

                {/* Breakdown Tables */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card title={t('usage-by-model', 'Usage by Model')}>
                            <Table
                                dataSource={transformBreakdownData(usageData?.by_model_with_names)}
                                columns={modelColumns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card title={t('usage-by-field', 'Usage by Field')}>
                            <Table
                                dataSource={transformBreakdownData(usageData?.by_field_with_names)}
                                columns={fieldColumns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card title={t('usage-by-user', 'Usage by User')}>
                            <Table
                                dataSource={transformBreakdownData(usageData?.by_user_with_names)}
                                columns={userColumns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Add Budget Modal */}
                <Modal
                    title={t('add-composable-ai-budget', 'Add Composable AI Budget')}
                    open={addBudgetModalOpen}
                    onOk={handleAddBudget}
                    onCancel={() => setAddBudgetModalOpen(false)}
                    confirmLoading={addingBudget}
                    okText={t('add-budget', 'Add Budget')}
                >
                    <Space direction="vertical" size="middle" style={{width: '100%'}}>
                        <Text>
                            {t('add-composable-ai-budget-description', 'Enter the amount to add to your Composable AI budget. You will be redirected to a secure payment page.')}
                        </Text>
                        <div>
                            <Text strong>{t('amount', 'Amount')}:</Text>
                            <InputNumber
                                style={{width: '100%', marginTop: '8px'}}
                                prefix={<DollarOutlined />}
                                min={1}
                                max={10000}
                                value={budgetAmount}
                                onChange={setBudgetAmount}
                                precision={2}
                            />
                        </div>
                        <Text type="secondary">
                            {t('budget-charge-note', 'You will be redirected to Stripe Checkout to complete the payment.')}
                        </Text>
                    </Space>
                </Modal>
            </Space>
        </Skeleton>
    );
};

export default AiUsageReport;
